import "./ClientRegistrationWaiverAndReleaseOfLiabilityComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {ImageConfig, Misc} from "../../../constants";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SuccessPopupComponent from "../../../shared/components/success-popup/SuccessPopupComponent";
import {useNavigate} from "react-router-dom";
import IconButtonComponent from "../../../shared/components/icon-button/IconButtonComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {getClientSignatureDetails} from "../../../store/actions/client.action";
import {setLoggedInUserData} from "../../../store/actions/account.action";

interface ClientRegistrationWaiverAndReleaseOfLiabilityComponentProps {
    onNext: () => void;
    appointmentId: any
}

const clientRegistrationWaiverAndReleaseOfLiabilityValidationSchema = Yup.object({
    client_name: Yup.string().required('Name is required'),
    date: Yup.string().nullable(),
    agreed_to_terms: Yup.boolean()
        .oneOf([true], 'You must agree to the terms'),
    agreed_to_waiver_expiry: Yup.boolean()
        .oneOf([true], 'You must agree to the waiver expiry'),
    agreed_to_renew_waiver: Yup.boolean()
        .oneOf([true], 'You must agree to the waiver renewal'),
    is_authorized_representative: Yup.boolean(),
    representative_name: Yup.string().when("is_authorized_representative", {
        is: true,
        then: Yup.string().required('Authorized Representative Name is required')
    }),
    signature: Yup.string().required()
});

const ClientRegistrationWaiverAndReleaseOfLiabilityFormInitialValues: any = {
    client_name: "",
    date: new Date(),
    agreed_to_terms: false,
    agreed_to_waiver_expiry: false,
    agreed_to_renew_waiver: false,
    is_authorized_representative: false,
    representative_name: "",
    signature: ""
}

const ClientRegistrationWaiverAndReleaseOfLiabilityComponent = (props: ClientRegistrationWaiverAndReleaseOfLiabilityComponentProps) => {
    const {appointmentId} = props;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [clientWaiverAndReleaseOfLiabilityFormInitialValues, setClientWaiverAndReleaseOfLiabilityFormInitialValues] = useState<any>(_.cloneDeep(ClientRegistrationWaiverAndReleaseOfLiabilityFormInitialValues));
    const [isFormSubmissionSuccessful, setIsFormSubmissionSuccessful] = useState<boolean>(false);
    const [isWaiverAcknowledgementModalOpen, setIsWaiverAcknowledgementModalOpen] = useState<boolean>(false);
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const formikRef: any = React.useRef();
    const {clientSignatureDetails} = useSelector((state: IRootReducerState) => state.client);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [getWaiverData, setGetWaiverData] = useState<any>([]);
    const {
        clientBasicDetails
    } = useSelector((state: IRootReducerState) => state.client);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = _.cloneDeep({
            ...values,
            appointment_id: appointmentId ? appointmentId : currentUser?.appointment_id
        });
        payload.date = CommonService.convertDateFormat(payload.date, "MM-DD-YYYY");
        payload.is_onboard_waiver_form_filled = true;
        CommonService._client.ClientWaiverAndReleaseOfLiability(payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setSubmitting(false);
                setIsFormSubmissionSuccessful(true);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [currentUser?.appointment_id, appointmentId]);

    const getWaiverAndLiabilityData = useCallback(() => {
        try {
            CommonService._client.getWaiverAndReleaseOfLiability({})
                .then((response: any) => {
                    setGetWaiverData(response.data);
                }).catch((error: any) => {
            })
        } catch (e) {
            console.log("error", e);
        }
    }, []);

    useEffect(() => {
        getWaiverAndLiabilityData();
    }, [getWaiverAndLiabilityData]);


    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);

    useEffect(() => {
        dispatch(getClientSignatureDetails(true, appointmentId))
    }, [dispatch, appointmentId]);

    useEffect(() => {
        if (clientSignatureDetails && currentUser) {
            setClientWaiverAndReleaseOfLiabilityFormInitialValues((prevValues: any) => ({
                ...prevValues,
                client_name: clientSignatureDetails?.client_name,
                is_authorized_representative: clientSignatureDetails?.is_authorized_representative,
                representative_name: clientSignatureDetails?.representative_name,
                signature: clientSignatureDetails?.signature,
                agreed_to_terms: getWaiverData?.agreed_to_terms,
                agreed_to_waiver_expiry: getWaiverData?.agreed_to_waiver_expiry,
                agreed_to_renew_waiver: getWaiverData?.agreed_to_renew_waiver,
            }));
        }
    }, [clientSignatureDetails, currentUser, getWaiverData]);


    useEffect(() => {
        if (currentUser?.onboarding_forms_type === 'default') {
            formikRef.current.setFieldValue('client_name', clientBasicDetails?.first_name + ' ' + clientBasicDetails?.last_name);
        }
    }, [clientBasicDetails, currentUser]);

    return (
        <div
            className={'client-registration-waiver-and-release-of-liability-component client-registration-details-block'}>
            <div className="client-registration-details-block__header">
                <div className="client-registration-details-block__title">
                    Waiver and Release of Liability
                </div>
            </div>
            <Formik
                validationSchema={clientRegistrationWaiverAndReleaseOfLiabilityValidationSchema}
                initialValues={clientWaiverAndReleaseOfLiabilityFormInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                innerRef={formikRef}
                validateOnMount={true}
                onSubmit={onSubmit}
            >
                {({values, validateForm, touched, errors, isValid, isSubmitting, setFieldValue}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form className="t-form" noValidate={true}>
                            {/*<FormDebuggerComponent values={values} errors={errors} showDebugger={true}/>*/}
                            <div className="t-form-controls">

                                {/*1*/}
                                <div className="waiver-section">
                                    <div className="waiver-content">
                                        <span
                                            className="bold">This Agreement</span> acknowledges that rehabilitation
                                        (physical or athletic) therapy,
                                        fitness or performance training, wellness care, and other related activities can
                                        involve physical, emotional, and psychological exertion, strain, and the
                                        potential risk of injury. By participating in any activities at Kinergy Sports
                                        Medicine and Performance (hereafter referred to as “KINERGY”), you do so
                                        entirely at your own risk and agree to assume all risks associated with your
                                        participation.
                                    </div>
                                    <div className="waiver-title">1. Acknowledgement of Risk</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        You understand and acknowledge that athletic or physical therapy and training
                                        activities, including but not limited to, exercise routines, strength training,
                                        rehabilitation exercises, wellness training, and other activities offered at
                                        KINERGY, can be strenuous. These activities carry an inherent risk of injury,
                                        illness, or even death, including the possibility of accidents or the sudden
                                        malfunctioning of equipment.
                                    </div>
                                    <div className="waiver-content mrg-left-15 mrg-top-20">
                                        You agree to voluntarily participate in these activities and assume all risks
                                        associated with such activities, including but not limited to injury, accidents,
                                        or loss of personal property. You also acknowledge that you are fully
                                        responsible for any personal belongings or property brought to KINERGY.
                                    </div>
                                </div>

                                {/*2*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">2. Medical Clearance</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        KINERGY strongly recommends that you obtain medical clearance from a licensed
                                        healthcare professional before using any exercise equipment or participating in
                                        any activities at KINERGY, particularly if you have any existing medical
                                        conditions, concerns, or a history of injury. KINERGY may, at its discretion,
                                        request medical clearance or advise against participation if deemed necessary
                                        for your safety.
                                    </div>
                                </div>

                                {/*3*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">3. Personal Responsibility</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        You acknowledge that it is your responsibility to inform KINERGY's staff of any
                                        existing medical conditions, injuries, limitations, or concerns that may affect
                                        your ability to participate in any activities offered at KINERGY. You confirm
                                        that you are physically and mentally capable of participating in the activities
                                        you choose to engage in at KINERGY.
                                    </div>
                                </div>

                                {/*4*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">4. Release of Liability</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        By signing this waiver, you, on behalf of yourself, your heirs, executors,
                                        personal representatives, and assigns, hereby release, waive, and discharge
                                        KINERGY, its owners, employees, agents, instructors, and affiliates from any and
                                        all liability for any injuries, damages, claims, or losses, including but not
                                        limited to personal injury, property damage, or loss, that may arise from your
                                        participation in any activity at KINERGY, whether caused by negligence, breach
                                        of contract, or otherwise.
                                    </div>
                                    <div className="waiver-content mrg-left-15 mrg-top-20">
                                        This release applies to all claims or causes of action, including but not
                                        limited to negligence, personal injury, or property damage, which may result
                                        from your use of the facility, its equipment, or participation in its programs.
                                    </div>
                                </div>

                                {/*5*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">5. Indemnification</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        You agree to indemnify and hold harmless KINERGY, its owners, employees,
                                        instructors, agents, and affiliates from any and all claims, demands, actions,
                                        liabilities or lawsuits arising out of your participation in activities at
                                        KINERGY, including any claims made by third parties due to your actions or
                                        participation.
                                    </div>
                                </div>

                                {/*6*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">6. Medical Emergency</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        In the event of a medical emergency, you authorize KINERGY's staff to perform or
                                        seek emergency medical treatment on your behalf, if necessary. You agree to bear
                                        all costs associated with such treatment and release KINERGY from any liability
                                        in this regard.
                                    </div>
                                </div>

                                {/*7*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">7. Severability</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        If any provision of this Waiver and Release of Liability is found to be invalid
                                        or unenforceable by a court of competent jurisdiction, the remaining provisions
                                        shall remain in full force and effect, and the offending provisions or
                                        provisions severed from this Agreement.
                                    </div>

                                </div>

                                {/*8*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">8. Duration and Renewal</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        This Waiver and Release of Liability shall remain valid for one (1) year from
                                        the date of signing. After this period, you will be required to sign a new
                                        Waiver and Release of Liability form in order to continue participation in
                                        activities at KINERGY.
                                    </div>
                                </div>

                                {/*9*/}
                                <div className="waiver-section">
                                    <div className="waiver-title">9. Governing Law</div>
                                    <div className="waiver-content mrg-left-15 mrg-top-5">
                                        This Agreement will be governed by and construed in accordance with the laws of
                                        the state or jurisdiction in which KINERGY operates. Any disputes arising from
                                        this Agreement shall be subject to the exclusive jurisdiction of the courts in
                                        that jurisdiction.
                                    </div>
                                </div>

                                <div className="waiver-section">
                                    <div className="waiver-title">Acknowledgement and Agreement</div>
                                    <div className="waiver-content mrg-top-5">
                                        By signing below, you acknowledge that you have read, fully understand, and
                                        voluntarily agree to the terms and conditions of this Waiver and Release of
                                        Liability. You understand that this document is legally binding, and you are
                                        waiving certain legal rights, including the right to sue. You further
                                        acknowledge that this Agreement cannot be modified orally, and no oral
                                        representations have been made to alter its meaning.
                                    </div>
                                </div>


                                <div className="ts-row pdd-top-20">
                                    <div className="ts-col-12">
                                        <Field name={'agreed_to_terms'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={"I acknowledge that I have read and agree to the statement as written above."}
                                                        required={true}
                                                        formikField={field}
                                                        color={'accent-color'}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-12">
                                        <Field name={'agreed_to_waiver_expiry'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={'I understand that this Waiver and Release of Liability form will remain valid for one (1) year from the effective date.'}
                                                        required={true}
                                                        formikField={field}
                                                        color={'accent-color'}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-12">
                                        <Field name={'agreed_to_renew_waiver'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={'I also understand that I will need to sign a new Waiver and Release of Liability form upon the expiration of this form. '}
                                                        required={true}
                                                        formikField={field}
                                                        color={'accent-color'}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-12">
                                        <Field name={'is_authorized_representative'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                        formikField={field}
                                                        color={'accent-color'}
                                                        onChange={() => {
                                                            setFieldValue('representative_name', '');
                                                        }}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                {
                                    values.is_authorized_representative && <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'representative_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Authorized Representative Name'}
                                                            placeholder={'Enter Representative Name'}
                                                            required={true}
                                                            fullWidth={true}
                                                            formikField={field}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                }
                                <div className="ts-row">
                                    <div className="ts-col-md-12 ts-col-lg-6">
                                        <Field name={'client_name'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"name_input"}
                                                        label={"Client's Name"}
                                                        placeholder={"Enter Client's Name"}
                                                        required={true}
                                                        disabled={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        titleCase={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="ts-col-md-12 ts-col-lg-6">
                                        <Field name={'date'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        id={"dob_input"}
                                                        label={'Date'}
                                                        placeholder={'Enter Date'}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        disabled={true}
                                                        readOnly={true}
                                                        // clearable={false}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                {
                                    !values?.signature &&
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <FormControlLabelComponent
                                                className={"font-weight-thin"}
                                                label={"Signature of Client or Authorized Representative"}
                                                required={true}/>
                                            <ButtonComponent
                                                onClick={() => {
                                                    setIsSignaturePopupOpen(true);
                                                }}
                                                prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                variant={'link'}
                                                className={'add-signature-button'}
                                                color={'info'}
                                                size={'medium'}
                                            >
                                                Add Signature
                                            </ButtonComponent>
                                        </div>
                                        {
                                            (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                            <ErrorComponent
                                                errorText={(_.get(errors, "signature"))}/>
                                        }
                                    </div>
                                }
                                {values?.signature &&
                                    <>
                                        <ESignApprovalComponent isSigned={true}
                                                                signature_url={values?.signature}
                                        />
                                        <div className={'signature-remove-button'}>
                                            <LinkComponent onClick={() => {
                                                setFieldValue('signature', "");
                                            }
                                            }>
                                                Remove Signature
                                            </LinkComponent>
                                        </div>
                                    </>
                                }
                                <div className="t-form-actions">
                                    <ButtonComponent
                                        id={"submit_btn"}
                                        size={'large'}
                                        type={"submit"}
                                        disabled={!isValid || isSubmitting}
                                        isLoading={isSubmitting}
                                    >
                                        Submit
                                    </ButtonComponent>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            <ModalComponent isOpen={isFormSubmissionSuccessful}
                            size={'xs'}
                            className=""
                            onClose={() => setIsFormSubmissionSuccessful(false)}>
                <div className="modal__content">
                    <SuccessPopupComponent
                        title={'All Done!'}
                        description={"Congratulations, you have completed your\n" +
                            "Portal Registration."}
                        imageData={ImageConfig.Tick}
                        actions={<>
                            <div className={'success-popup-continue-and-cancel-cta'}>
                                <ButtonComponent onClick={() => {
                                    const modifiedUser = {
                                        ...currentUser,
                                        is_profile_completed: true, // this will overwrite the existing `role` key
                                    };
                                    dispatch(setLoggedInUserData(modifiedUser));
                                    navigate(CommonService._routeConfig.AppointmentList())
                                }}>
                                    Proceed
                                </ButtonComponent>
                            </div>
                        </>}
                    />
                </div>
            </ModalComponent>


            <ModalComponent isOpen={isWaiverAcknowledgementModalOpen}
                            className="privacy-policy-modal"
                            onClose={() => setIsWaiverAcknowledgementModalOpen(false)}>
                <div className="privacy-policy-modal__content">
                    <div className="privacy-policy-modal__content_left">
                        <div className="privacy-policy-modal__content_icon">
                            <ImageConfig.PrivacyPolicyIcon/>
                        </div>
                        <div className="privacy-policy-modal__content_title">
                            Waiver and Release<br/>
                            of Liability

                        </div>
                    </div>
                    <div className="privacy-policy-modal__content_right">
                        <div className="privacy-policy-modal__content_right__header">
                            <IconButtonComponent id={'close_btn'} onClick={
                                () => setIsWaiverAcknowledgementModalOpen(false)
                            }>
                                <ImageConfig.CloseIcon/>
                            </IconButtonComponent>
                        </div>
                        <div className="privacy-policy-modal__content_right__block">
                            <div className="privacy-policy-modal__content_right__block__title">
                                Our Waiver and Release of Liability
                            </div>

                        </div>
                    </div>
                </div>
            </ModalComponent>
            <ModalComponent isOpen={isSignaturePopupOpen}
                            onClose={handleSignaturePopupClose}
                            size={"xl"}
            >
                <SignaturePopupComponent
                    onSign={(signImage: any) => {
                        formikRef?.current.setFieldValue('signature', signImage)
                        // setRegistrationFormInitialValues({
                        //     ...formikRef?.current?.values,
                        //     signature: signImage
                        // })
                        handleSignaturePopupClose();
                    }}
                    handleSignaturePopupClose={handleSignaturePopupClose}
                />
            </ModalComponent>
        </div>
    );

};

export default ClientRegistrationWaiverAndReleaseOfLiabilityComponent;
